// Minimal markdown -> HTML renderer for lesson explanations. Only
// covers what we actually use in lesson bodies: ATX headers, paragraphs,
// inline `code`, **bold**, *italic*, and fenced ```sas``` code blocks.
// Pulling in a full markdown library would add ~50 KB for features we
// don't need; this 60-line renderer ships zero deps.

const HTML_ESC: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESC[c]);
}

/// Render a small subset of markdown to HTML. Block forms: ATX headers
/// (`#`..`######`), fenced code (```lang ... ```), paragraphs (blank-
/// line separated). Inline forms: `code`, **bold**, *italic*.
export function renderMarkdown(src: string): string {
  const lines = src.split('\n');
  const html: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      const cls = lang ? ` class="lang-${escapeHtml(lang)}"` : '';
      html.push(`<pre><code${cls}>${escapeHtml(buf.join('\n'))}</code></pre>`);
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const lvl = heading[1].length;
      html.push(`<h${lvl}>${renderInline(heading[2])}</h${lvl}>`);
      i++;
      continue;
    }
    if (line.trim().length === 0) {
      i++;
      continue;
    }
    const paragraph: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim().length > 0 &&
      !lines[i].startsWith('```') &&
      !/^#{1,6}\s+/.test(lines[i])
    ) {
      paragraph.push(lines[i]);
      i++;
    }
    html.push(`<p>${renderInline(paragraph.join(' '))}</p>`);
  }
  return html.join('\n');
}

/// Inline rules applied to the text content of a paragraph or heading.
/// Order matters: fence-style `code` has highest priority (so ** inside
/// `code` doesn't bold), then **bold**, then *italic*. Code spans are
/// stashed behind a sentinel marker (private-use Unicode codepoints)
/// so the bold/italic regexes can't reach inside them.
const SENT_OPEN = ''; // Private Use Area — won't appear in user text
const SENT_CLOSE = '';

function renderInline(s: string): string {
  const codeChunks: string[] = [];
  let work = s.replace(/`([^`]+)`/g, (_m, body: string) => {
    codeChunks.push(`<code>${escapeHtml(body)}</code>`);
    return `${SENT_OPEN}${codeChunks.length - 1}${SENT_CLOSE}`;
  });
  work = escapeHtml(work);
  work = work.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  work = work.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  work = work.replace(
    new RegExp(`${SENT_OPEN}(\\d+)${SENT_CLOSE}`, 'g'),
    (_m, idx: string) => codeChunks[Number(idx)],
  );
  return work;
}
